// src/pages/checks/CheckBusinessLicense.js
import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../../component/Layout";
import { useNavigate } from "react-router-dom";
import api from "../../axios";
import {
  GoogleMap,
  LoadScript,
  Marker,
  MarkerClusterer,
  InfoWindow,
} from "@react-google-maps/api";
import { cityData } from "../../data/cityData";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const CheckBusinessLicense = () => {
  const navigate = useNavigate();
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ city: "", district: "", type: "" });
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("");
  const [center, setCenter] = useState({ lat: 39.9208, lng: 32.8541 });
  const [showMap, setShowMap] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [infoWindow, setInfoWindow] = useState(null);
  const rowRefs = useRef({});
  const [loadingData, setLoadingData] = useState(false);

  // Bulk WhatsApp
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [messageText, setMessageText] = useState("");

  // Modal for warnings
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const username = sessionStorage.getItem("username");
    if (!username) {
      navigate("/login");
      return;
    }
    fetchLicenses(username);
  }, []);

  const showWarning = (msg) => {
    setModalText(msg);
    setModalOpen(true);
  };

  const fetchLicenses = async (username) => {
    try {
      const res = await api.get(`/api/license/by-username/${username}`);
      const licenses = res.data || [];
      const matched = licenses.find(
        (l) => l.softwareName === "İşletme Radar" && l.isActive
      );
      if (!matched) {
        setUnauthorized(true);
        setTimeout(() => navigate("/dashboard"), 3000);
      }
    } catch (error) {
      console.error("Lisans kontrol hatası:", error);
      setUnauthorized(true);
      setTimeout(() => navigate("/dashboard"), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    // Yeni sorgu başladığında önce tüm seçimleri temizle
    setSelectedPhones([]);

    if (!form.city || !form.type) {
      showWarning("Lütfen il ve işletme türü giriniz.");
      return;
    }
    setLoadingData(true);
    try {
      const query = `${form.type} in ${form.district}, ${form.city}`;
      const response = await api.get("/api/settings/proxy/google-places", {
        params: { query, softwareId: 6 },
      });
      const apiResults = response.data.results || [];
      const enriched = apiResults.map((place, idx) => ({
        id: idx + 1,
        name: place.name,
        address: place.address,
        rating: place.rating || "-",
        phoneNumber: place.phoneNumber || "-",
        website: place.website || "-",
        latitude: place.latitude,
        longitude: place.longitude,
      }));
      if (
        enriched.length > 0 &&
        enriched[0].latitude != null &&
        enriched[0].longitude != null
      ) {
        setCenter({
          lat: enriched[0].latitude,
          lng: enriched[0].longitude,
        });
      }
      setResults(enriched);
      setCurrentPage(1);
    } catch (error) {
      console.error("Proxy Google Places API hatası:", error);
      showWarning("Google API'den sonuç alınamadı.");
    } finally {
      setLoadingData(false);
    }
  };

  const filteredResults = results.filter(
    (item) =>
      item.name.toLowerCase().includes(filter.toLowerCase()) ||
      item.address.toLowerCase().includes(filter.toLowerCase())
  );

  const paginatedResults = filteredResults.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredResults);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sonuclar");
    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, "sonuclar.xlsx");
  };

  const togglePhoneSelection = (phone) => {
    if (phone === "-") return;
    setSelectedPhones((prev) =>
      prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone]
    );
  };

  const sendWhatsAppBulk = () => {
    if (!messageText.trim() || selectedPhones.length === 0) {
      showWarning(
        "Mesaj ve en az bir geçerli telefon numarası seçmeniz gerekiyor."
      );
      return;
    }
    selectedPhones.forEach((phone) => {
      const clean = phone.replace(/\D/g, "");
      const url = `https://wa.me/${clean}?text=${encodeURIComponent(
        messageText
      )}`;
      window.open(url, "_blank");
    });
  };

  useEffect(() => {
    if (selectedId && rowRefs.current[selectedId]) {
      rowRefs.current[selectedId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedId]);

  return (
    <>
      <Helmet>
        <title>İşletmeRadar</title>
      </Helmet>
      <Layout pageTitle="İşletmeRadar">
        {/* Warning Modal */}
        {modalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-auto">
              <h2 className="text-xl font-semibold mb-4">Uyarı</h2>
              <p className="mb-6">{modalText}</p>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={() => setModalOpen(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-10 text-gray-600">Yükleniyor...</div>
        ) : unauthorized ? (
          <div className="rounded-md bg-red-50 p-4 max-w-xl mx-auto mt-6">
            <div className="text-red-700">
              İşletme Radar yazılımı için lisansınız bulunmamaktadır. Anasayfaya
              yönlendiriliyorsunuz...
            </div>
          </div>
        ) : (
          <div className="p-6 bg-white rounded shadow-md relative">
            {/* Loading Spinner */}
            {loadingData && (
              <div className="fixed inset-0 z-40 bg-black bg-opacity-40 flex items-center justify-center">
                <div className="bg-white p-6 rounded shadow-lg text-center">
                  <div className="animate-spin h-8 w-8 mx-auto border-4 border-blue-400 border-t-transparent rounded-full mb-4"></div>
                  <p className="text-gray-700">Veriler sorgulanıyor...</p>
                </div>
              </div>
            )}

            {/* Search Inputs */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <select
                className="border p-2 rounded w-full"
                value={selectedCity}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setSelectedDistrict("");
                  setForm({ ...form, city: e.target.value, district: "" });
                }}
              >
                <option value="">İl Seçiniz</option>
                {Object.keys(cityData).map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              <select
                className="border p-2 rounded w-full"
                value={selectedDistrict}
                onChange={(e) => {
                  setSelectedDistrict(e.target.value);
                  setForm({ ...form, district: e.target.value });
                }}
                disabled={!selectedCity}
              >
                <option value="">İlçe Seçiniz</option>
                {selectedCity &&
                  cityData[selectedCity]?.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
              </select>
              <input
                type="text"
                placeholder="İşletme Türü (örneğin: kafe, eczane)"
                list="typeOptions"
                className="border p-2 rounded w-full"
                value={form.type}
                onChange={(e) =>
                  setForm({ ...form, type: e.target.value })
                }
              />
              <datalist id="typeOptions">
                <option value="kafe" />
                <option value="eczane" />
                <option value="market" />
                <option value="lokanta" />
                <option value="berber" />
                <option value="otel" />
              </datalist>
            </div>

            {/* Buttons */}
            <div className="flex justify-between items-center mb-4">
              <div className="space-x-2">
                <button
                  onClick={() => setShowMap(!showMap)}
                  className="px-4 py-2 border text-sm rounded text-indigo-600 border-indigo-600 hover:bg-indigo-50"
                >
                  {showMap ? "Haritayı Gizle" : "Haritayı Göster"}
                </button>
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 border text-sm rounded text-green-600 border-green-600 hover:bg-green-50"
                >
                  Excel
                </button>
              </div>
              <button
                onClick={handleSearch}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
              >
                Sorgula
              </button>
            </div>

            {/* Bulk WhatsApp */}
            {paginatedResults.length > 0 && (
              <div className="mb-4">
                <textarea
                  className="w-full border p-2 rounded"
                  rows={3}
                  placeholder="Göndermek istediğiniz mesajı yazın..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                />
                <button
                  onClick={sendWhatsAppBulk}
                  className="mt-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  Seçililere WhatsApp Gönder
                </button>
              </div>
            )}

            {/* Map */}
            {showMap && paginatedResults.length > 0 && (
              <div className="mb-6 rounded overflow-hidden">
                <LoadScript googleMapsApiKey="">
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={13}
                  >
                    <MarkerClusterer>
                      {(clusterer) =>
                        paginatedResults.map(
                          (place) =>
                            place.latitude != null &&
                            place.longitude != null && (
                              <Marker
                                key={place.id}
                                clusterer={clusterer}
                                position={{
                                  lat: place.latitude,
                                  lng: place.longitude,
                                }}
                                onClick={() => {
                                  setSelectedId(place.id);
                                  setInfoWindow(place);
                                }}
                              />
                            )
                        )
                      }
                    </MarkerClusterer>
                    {infoWindow && (
                      <InfoWindow
                        position={{
                          lat: infoWindow.latitude,
                          lng: infoWindow.longitude,
                        }}
                        onCloseClick={() => setInfoWindow(null)}
                      >
                        <div>
                          <h3 className="font-bold">{infoWindow.name}</h3>
                          <p className="text-sm">{infoWindow.address}</p>
                        </div>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                </LoadScript>
              </div>
            )}

            {/* Table */}
            {paginatedResults.length > 0 && (
              <>
                <input
                  type="text"
                  placeholder="Tabloda ara..."
                  className="mb-4 w-full border p-2 rounded"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="px-4 py-2 border-b">Seç</th>
                        <th className="px-4 py-2 border-b">#</th>
                        <th className="px-4 py-2 border-b">İşletme Adı</th>
                        <th className="px-4 py-2 border-b">Adres</th>
                        <th className="px-4 py-2 border-b">Puan</th>
                        <th className="px-4 py-2 border-b">Telefon</th>
                        <th className="px-4 py-2 border-b">Web</th>
                        <th className="px-4 py-2 border-b">Konum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedResults.map((place) => (
                        <tr
                          key={place.id}
                          ref={(el) => (rowRefs.current[place.id] = el)}
                          onClick={() => {
                            setSelectedId(place.id);
                            setInfoWindow(place);
                          }}
                          className={`hover:bg-gray-50 cursor-pointer ${
                            selectedId === place.id ? "bg-yellow-100" : ""
                          }`}
                        >
                          <td className="px-4 py-2 border-b">
                            <input
                              type="checkbox"
                              disabled={place.phoneNumber === "-"}
                              checked={selectedPhones.includes(place.phoneNumber)}
                              onChange={() =>
                                togglePhoneSelection(place.phoneNumber)
                              }
                            />
                          </td>
                          <td className="px-4 py-2 border-b">{place.id}</td>
                          <td className="px-4 py-2 border-b font-medium">
                            {place.name}
                          </td>
                          <td className="px-4 py-2 border-b">{place.address}</td>
                          <td className="px-4 py-2 border-b">{place.rating}</td>
                          <td className="px-4 py-2 border-b">
                            {place.phoneNumber}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {place.website !== "-" ? (
                              <a
                                href={place.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                              >
                                Siteye Git
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {place.latitude != null && place.longitude != null ? (
                              <a
                                href={`https://www.google.com/maps?q=${place.latitude},${place.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                              >
                                Harita
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-center items-center mt-4 space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.max(p - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  <span>
                    {currentPage} /{" "}
                    {Math.ceil(filteredResults.length / itemsPerPage)}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        p < Math.ceil(filteredResults.length / itemsPerPage)
                          ? p + 1
                          : p
                      )
                    }
                    disabled={
                      currentPage >=
                      Math.ceil(filteredResults.length / itemsPerPage)
                    }
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Layout>
    </>
  );
};

export default CheckBusinessLicense;
